import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { OwnerWorkspaceMetrics } from 'config/api/assetRopa/assetRopaWorkspace.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

type Props = {
  metrics?: OwnerWorkspaceMetrics;
};

const OwnerWorkspaceMetricsSection: React.FC<Props> = ({ metrics }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.metrics;

  const metricItems = useMemo(
    () => [
      { key: 'gdprRelevantAssetsCount' as const, label: t(messages.gdpr_relevant_assets), note: t(messages.owner_scope_note) },
      { key: 'assetsWithoutProcessingActivitiesCount' as const, label: t(messages.without_processing_activities) },
      { key: 'incompleteProcessingsCount' as const, label: t(messages.incomplete_processings) },
      { key: 'dpiaIndicationsCount' as const, label: t(messages.dpia_indications) },
      { key: 'thirdCountryProcessingCount' as const, label: t(messages.third_country_processing) },
      { key: 'sensitivePersonalDataCount' as const, label: t(messages.sensitive_personal_data) },
    ],
    [messages, t],
  );

  if (!metrics) return null;

  return (
    <section aria-label={t(messages.aria_label)} className={styles.ownerWorkspaceMetricsGrid}>
      {metricItems.map(item => (
        <Box key={item.key} aria-label={`${item.label}: ${metrics[item.key]}`} className={styles.metricCard} role='group'>
          <Typography className={styles.metricLabel} color='textSecondary' variant='body2'>
            {item.label}
          </Typography>
          <div className={styles.metricValueLine}>
            <Typography className={styles.metricValue} component='div' variant='h4'>
              {metrics[item.key]}
            </Typography>
          </div>
          {item.note && (
            <Typography className={clsx(styles.metricLabel, styles.metricNote)} color='textSecondary' variant='caption'>
              {item.note}
            </Typography>
          )}
        </Box>
      ))}
    </section>
  );
};

export default OwnerWorkspaceMetricsSection;
